<template>
  <RevContentBlock
    v-if="hasNoItems"
    :button-label="i18n(translations.noItemsButton)"
    data-qa="empty-cart-block"
    :image-props="{
      alt: '',
      height: 463,
      src: '/img/checkout/emptyBasket.svg',
      style: {
        height: 'auto',
        margin: '0 auto',
        width: 'auto',
      },
      width: 438,
    }"
    :surtitle="i18n(translations.noItemsSurtitle)"
    :title="i18n(translations.noItemsTitle)"
    :to="resolveLocalizedRoute({ name: HOME })"
  >
    <p class="body-1">
      {{ i18n(translations.noItemsDescription) }}
    </p>
  </RevContentBlock>

  <div v-if="hasItems">
    <ClientOnly>
      <Teleport to="#checkout-side-cart-submit">
        <RevButton
          class="mt-24"
          data-qa="go-to-next-side"
          :disabled="isNextButtonDisabled"
          full-width="always"
          :loading="isNextButtonLoading"
          variant="primary"
          @click="nextStep"
        >
          {{ i18n(translations.buttonCheckout) }}
        </RevButton>
        <div class="caption mt-4 flex items-center justify-center">
          <IconLockLocked class="mr-4 h-16 w-16 self-baseline" />

          {{ i18n(translations.securePayment) }}
        </div>
      </Teleport>
    </ClientOnly>

    <CheckoutNext
      :disabled="isNextButtonDisabled"
      :loading="isNextButtonLoading"
      placement="top"
      @next="nextStep"
    />

    <h1 class="heading-3 mb-12 mt-24 md:mb-24 md:mt-0">
      {{ i18n(translations.mainTitle) }}
    </h1>

    <RevCard
      v-for="item in cartStore.items"
      :id="item.id"
      :key="item.id"
      class="mb-12 p-24 md:mb-56"
    >
      <CartProduct
        :data-qa="'cart-product-' + item.id"
        display-link
        :has-no-stock="!isItemAvailable(item.listingId)"
        :image="item.image"
        :link="getItemLink(item.listingId)"
        :title="item.title"
      >
        <template #default>
          <div v-if="item.gradeName" class="flex">
            <p class="body-2">{{ i18n(translations.productGrade) }}</p>
            <p class="body-2 ml-4">{{ getGradeName(item) }}</p>
          </div>

          <AccessoriesSummary :accessories="item.providedAccessories" />

          <p v-if="isAccessory(item.category)" class="body-2">
            {{ i18n(translations.cartColor) }} {{ item.color }}
          </p>

          <div class="mb-8 flex md:my-8">
            <RevLink class="text-left" @click="onClickWarrantyButton">
              <span class="body-2">{{ i18n(translations.warranty) }}</span>
            </RevLink>
            <ClientOnly>
              <WarrantyModal :name="MODAL_NAMES.CART_WARRANTY" />
            </ClientOnly>
          </div>

          <Prices
            v-if="isItemAvailable(item.listingId)"
            class="body-1-bold"
            :original-price="legacyPrice(item.price)"
            :price="legacyPrice(item.price)"
            size="medium"
          />

          <InstallmentPaymentPrice
            v-if="shouldDisplayInstallmentPaymentPrice(item)"
            class="body-2"
            :monthly-payment-plan="item.installmentMonthlyPaymentPlan"
          />
        </template>

        <template #actions>
          <div class="my-20 flex justify-between md:my-0">
            <ArticleQuantitySelector
              v-if="isItemAvailable(item.listingId)"
              :id="`quantity-${item.listingId}`"
              :limit="10"
              :max="item.quantityMax"
              :model-value="desiredQuantityPerItem[item.listingId] || '1'"
              @update:model-value="
                (value) => handleQuantitySelectorChange(item, value)
              "
            />

            <RevButtonTiny
              class="ml-12"
              data-qa="delete-item-button"
              variant="secondaryDestructive"
              @click="handleDeleteButtonClick(item)"
            >
              {{ i18n(translations.productRemove) }}
            </RevButtonTiny>
          </div>
        </template>
      </CartProduct>

      <section v-if="isItemAvailable(item.listingId)">
        <div v-if="shouldDisplayInsurancesOffers(item)" class="mb-32 md:mb-56">
          <InsurancesOptions
            :ref="(el) => (insurancesRef[item.listingId] = el)"
            can-be-deselected
            :listing-id="item.listingId"
            :offers="item.insuranceOffers"
            :product-name="item.model"
            show-selected
            tracking-context="cart"
            @update-compliancy="handleInsuranceOptionsUpdateTerms"
            @update-selected-option="handleInsuranceOptionsUpdate"
          />
          <p
            v-if="errorTermsOfUse[item.listingId]"
            :id="`errorMessage-${item.listingId}`"
            class="text-static-danger-hi mt-3"
          >
            {{ i18n(translations.trustpackCGVError) }}
          </p>
        </div>

        <div
          v-for="option in item.options"
          :key="option.type"
          class="first:mt-0 md:mt-56"
        >
          <span class="body-1 mb-24 flex">
            {{ option.title }}
          </span>

          <ShippingOptions
            :listing-id="item.listingId"
            :option="option"
            :show-collection-point-error="hasCollectionPointError"
            @select="selectOption"
          />
        </div>
      </section>
    </RevCard>

    <SwapSummary
      v-if="swapStore.hasOffer"
      class="mb-56 mt-32"
      data-qa="swap-summary"
      :details="swapStore.offer.diagnosticSummary"
      :price="swapStore.offer.price"
      :title="swapStore.offer.title"
      @delete-swap="handleDeleteSwap"
    />

    <RevCard v-if="swapStore.hasAdvertisement" class="mb-56 mt-32 md:hidden">
      <SwapBlock
        compact
        :grade-id="0"
        :modal-name="BUYBACK_MODAL_NAMES.SWAP_CART"
        :model="swapAdvertisement.model"
        :price="swapAdvertisement.price"
        product-category=""
        shipping=""
        :swap-offer="swapAdvertisement"
        swap-redirection=""
        :swap-status="SWAP_SUCCESS"
        tracking-label="swap_from_cart"
        @confirmation="handleSwapConfirmation"
      />
    </RevCard>

    <div>
      <RevDivider v-if="isCrossSellVisible" class="my-40 md:my-56" />

      <RecommendationCarousel
        :options="{
          withCrossedPrice: true,
          withStartingFrom: false,
          withGrade: true,
          withAddToCart: true,
        }"
        :recommendation-query="{
          category: 'crossSellWeb',
          limit: 12,
          personalisation: true,
          scope: 'cart',
          scopeId: recommendationIds,
        }"
        :title="i18n(translations.titleCrossSell)"
        @loaded="handleCrossSellLoaded"
        @refresh="handleCrossSellRefresh"
      />

      <RevDivider v-if="isCrossSellVisible" class="my-40 md:my-56" />
    </div>

    <ReassuranceItems />

    <CheckoutNext
      :disabled="isNextButtonDisabled"
      :loading="isNextButtonLoading"
      placement="bottom"
      @next="nextStep"
    />

    <InsuranceCatchupModal
      v-if="shouldDisplayCatchupModal"
      :id="firstItemAvailableWithInsurances.listingId"
      class="z-10"
      :model="firstItemAvailableWithInsurances.model"
      :offers="firstItemAvailableWithInsurances.insuranceOffers"
      @continue="handleCatchupModalClose"
      @go-to="handleCatchupModalGoTo"
    />
  </div>
</template>

<script setup>
import { storeToRefs, useRoute, useRouter, useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { cartAPI, paymentAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useAuthStore } from '@backmarket/nuxt-module-oauth/useAuthStore'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevButtonTiny } from '@ds/components/ButtonTiny'
import { RevCard } from '@ds/components/Card'
import { RevContentBlock } from '@ds/components/ContentBlock'
import { RevDivider } from '@ds/components/Divider'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { IconLockLocked } from '@ds/icons/IconLockLocked'

import { Prices } from '#components'
import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { MODAL_NAMES as BUYBACK_MODAL_NAMES } from '~/scopes/buyback/constants'
import { SWAP_SUCCESS } from '~/scopes/buyback/swap/components/SwapBlock/SwapBlock.constants'
import SwapBlock from '~/scopes/buyback/swap/components/SwapBlock/SwapBlock.vue'
import SwapSummary from '~/scopes/buyback/swap/components/SwapSummary/SwapSummary.vue'
import { HOME } from '~/scopes/home/route-names'
import { InstallmentPaymentPrice } from '~/scopes/payment'
import RecommendationCarousel from '~/scopes/recommendation/components/RecommendationCarousel.vue'

import AccessoriesSummary from '../../components/AccessoriesSummary'
import ArticleQuantitySelector from '../../components/ArticleQuantitySelector'
import CartProduct from '../../components/CartProduct'
import CheckoutNext from '../../components/CheckoutNext'
import InsuranceCatchupModal from '../../components/InsuranceCatchupModal'
import InsurancesOptions from '../../components/InsurancesOptions/InsurancesOptions'
import ShippingOptions from '../../components/ShippingOptions'
import { DEBOUNCE_DELAY, MODAL_NAMES } from '../../config'
import { CHECKOUT } from '../../routes-names'
import { useCartStore } from '../../stores/cartStore'
import { useLoaderStore } from '../../stores/loaderStore'
import { useSwapStore } from '../../stores/swapStore'

import translations from './Cart.translations'

import ReassuranceItems from '~/scopes/reassurance/components/ReassuranceItems/ReassuranceItems'
import WarrantyModal from '~/scopes/reassurance/components/WarrantyModal/WarrantyModal'

const authStore = useAuthStore()
const cartStore = useCartStore()
const loaderStore = useLoaderStore()
const swapStore = useSwapStore()
const runtimeConfig = useRuntimeConfig()
const tracking = useTracking()
const router = useRouter()

const i18n = useI18n()
const route = useRoute()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const desiredQuantityPerItem = ref(
  cartStore.availableItems.reduce(
    (acc, { listingId, quantity }) => ({
      ...acc,
      [listingId]: String(quantity),
    }),
    {},
  ),
)
const itemPaymentMethods = ref({})
const selectedInsurances = ref(
  cartStore.items
    .filter((item) => item.quantity === 1 && item.insuranceOffers?.length > 0)
    .reduce(
      (acc, { listingId, insuranceOffers }) => ({
        ...acc,
        [listingId]: insuranceOffers.find((offer) => offer.selected),
      }),
      {},
    ),
)
const productWithSelectedInsurance = ref(
  Object.entries(selectedInsurances.value).reduce(
    (acc, [id, insurance]) =>
      !insurance?.defaultOffer ? { ...acc, [id]: insurance } : acc,
    {},
  ),
)
const hasSubmittedOnce = ref(false)
const errorTermsOfUse = ref({})
const insurancesRef = ref({})
const isCrossSellVisible = ref(false)
const hasCollectionPointError = ref(false)

const { isItemAvailable, getItemLink } = storeToRefs(cartStore)

const hasNoItems = computed(() => isEmpty(cartStore.items))
const hasItems = computed(() => !isEmpty(cartStore.items))

const isNextButtonLoading = computed(
  () =>
    !cartStore.availableItems.every(
      (item) =>
        parseInt(desiredQuantityPerItem.value[item.listingId], 10) ===
        item.quantity,
    ),
)
const isNextButtonDisabled = computed(
  () => !cartStore.hasAvailableItems || isNextButtonLoading.value,
)

const hasDefaultInsuranceSelected = computed(() => {
  const itemsWithDefaultInsurances = cartStore.availableItems.reduce(
    (acc, item) => {
      if (
        item.insuranceOffers?.length > 1 &&
        item.quantity === 1 &&
        item.insuranceOffers.some(
          (offer) => offer.selected && offer.defaultOffer,
        )
      ) {
        return [...acc, item]
      }

      return acc
    },
    [],
  )

  return itemsWithDefaultInsurances.length === 1
})

const firstItemAvailableWithInsurances = computed(() => {
  return (
    cartStore.availableItems.find(
      (item) =>
        item.insuranceOffers?.length > 1 &&
        item.quantity === 1 &&
        item.insuranceOffers.some(
          (offer) => offer.selected && offer.defaultOffer,
        ),
    ) || {}
  )
})

const shouldDisplayCatchupModal = computed(
  () =>
    !runtimeConfig.public.KILL_INSURANCE_CATCHUP_MODAL &&
    cartStore.showCatchupModal &&
    hasDefaultInsuranceSelected.value &&
    !isEmpty(firstItemAvailableWithInsurances.value),
)

const swapAdvertisement = computed(() => ({
  model:
    cartStore.availableItems.length === 1
      ? cartStore.availableItems[0].model
      : i18n(translations.swapMultipleItemsTitle),
  price: i18n.price(swapStore.advertisement?.cartPriceWithSwapDiscount ?? ''),
  isOffer: swapStore.hasAdvertisement,
}))

const recommendationIds = computed(() =>
  cartStore.items.reduce((acc, item) => `${acc},${item.listingId}`, ''),
)

const firstUncheckedCompliancyListingId = () => {
  return Object.keys(selectedInsurances.value).find((listingId) => {
    const insurance = selectedInsurances.value[listingId]

    return (
      insurance?.compliancy?.isUserAgreementRequired &&
      !insurance?.compliancy?.isUserAgreementAccepted
    )
  })
}

const nextStep = async ({ skipCatchupModal = false } = {}) => {
  tracking.trackClick({
    name: 'go_checkout',
    zone: 'cart',
  })

  hasSubmittedOnce.value = true

  // Check if collection point option has a selected collection point
  if (
    cartStore.isSelectedCollectionPointMissing &&
    cartStore.optionWithMissingCollectionPointSelected
  ) {
    hasCollectionPointError.value = true
    const firstOption = cartStore.optionWithMissingCollectionPointSelected

    document
      .getElementById(
        `cart-shipping-option-${firstOption[0]}-${firstOption[1][0]?.choice?.id}`,
      )
      .scrollIntoView({ block: 'center', behavior: 'smooth' })

    return
  }

  const listingId = firstUncheckedCompliancyListingId()

  // if position is defined then error
  if (listingId) {
    errorTermsOfUse.value[listingId] = true
    // scroll to the first article where the error is
    document
      .getElementById(`compliancy-${listingId}`)
      .scrollIntoView({ block: 'start', behavior: 'smooth' })

    // block nextStep if terms of use are not ok
    return
  }

  if (!skipCatchupModal && shouldDisplayCatchupModal.value) {
    openModal(MODAL_NAMES.CART_INSURANCE_OFFER_CATCHUP)

    // block nextStep if modal is opened
    return
  }

  try {
    if (authStore.authenticated) {
      // check auth state and proceed
      loaderStore.enable()
      await cartStore.fetchShippings()

      router.push({
        name: cartStore.isShippable
          ? CHECKOUT.ADDRESS_CONFIRMATION
          : CHECKOUT.SHIPPING_ADDRESS,
      })
    } else {
      router.push({
        name: CHECKOUT.AUTHENTICATION,
      })
    }
  } catch {
    // fail silently
  } finally {
    loaderStore.disable()
  }
}

const getGradeName = (item) => {
  if (item.hasNewBattery) {
    return i18n(translations.productGradeWithNewBattery, {
      grade: item.gradeName,
    })
  }

  return item.gradeName
}

// TODO [PI-1677] Add isAccessory (boolean) to Cart API
const isAccessory = (type) => {
  return i18n(translations.crossSellCategory) === type
}

const onClickWarrantyButton = () => {
  openModal(MODAL_NAMES.CART_WARRANTY)
}

const legacyPrice = (price) => {
  return {
    display: i18n.price(price),
    raw: parseFloat(price),
  }
}

const updateItem = async (item, action) => {
  try {
    await $httpFetch(cartAPI.postUpdateQuantity, {
      body: {
        action,
        listingId: item.listingId,
        newQuantity: item.quantity,
        listingPrice: item.price,
      },
    })
    if (action === 'delete') {
      tracking.trackRemoveFromCart({
        product: cartStore
          .trackingData(route.name)
          .products.find((product) => item.productId === product.id),
      })
    }

    await cartStore.fetchCart()

    tracking.trackFunnel(cartStore.trackingData(route.name))
  } catch {
    // Fails silently
  }

  try {
    // TODO: [PAYIN-2299] Remove this fetch when the payment methods API returns errors by listingId
    const result = $httpFetch(paymentAPI.getMethods, {
      queryParams: {
        listings: item.id,
        countryCode: useMarketplace().market.countryCode,
      },
    })

    itemPaymentMethods.value[item.id] = result.results
  } catch {
    // no fallback
  }
}

const deleteInsuranceSelected = (item) => {
  delete selectedInsurances.value[item.id]
  delete productWithSelectedInsurance.value[item.id]
}

const handleDeleteButtonClick = async (item) => {
  await updateItem(item, 'delete')
  delete desiredQuantityPerItem.value[item.listingId]
  deleteInsuranceSelected(item)
}

const handleQuantitySelectorChange = useDebounceFn(
  async function doUpdateQuantity(item, quantity) {
    desiredQuantityPerItem.value[item.listingId] = quantity
    await updateItem({ ...item, quantity }, 'update')
  },
  DEBOUNCE_DELAY,
  false,
)

const shouldDisplayInsurancesOffers = (item) => {
  return !isEmpty(item.insuranceOffers)
}

const handleInsuranceOptionsUpdate = async ({ id, insurance }) => {
  errorTermsOfUse.value[id] = false
  selectedInsurances.value[id] = insurance

  try {
    await $httpFetch(cartAPI.postUpdateInsuranceOffer, {
      body: {
        listingId: id,
        insuranceOfferId: insurance.id,
      },
    })

    await cartStore.fetchCart()
  } catch {
    // fail silently
  }
}

const handleInsuranceOptionsUpdateTerms = async ({ id, insurance }) => {
  errorTermsOfUse.value[id] = false
  selectedInsurances.value[id] = insurance
  try {
    await $httpFetch(cartAPI.postAcceptAgreement, {
      body: {
        listingId: id,
        insuranceOfferId: insurance.id,
      },
    })

    await cartStore.fetchCart()
  } catch {
    // fail silently
  }
}

const handleCatchupModalClose = () => {
  nextStep({ skipCatchupModal: true })
}

const handleCatchupModalGoTo = async ({ id }) => {
  insurancesRef.value[id]?.[0]?.$el?.scrollIntoView({
    block: 'center',
    behavior: 'smooth',
  })
}

const selectOption = async (body, choice) => {
  hasCollectionPointError.value = false

  try {
    await $httpFetch(cartAPI.postUpdateOption, {
      body,
    })

    tracking.trackClick({
      zone: 'cart',
      name: choice.shipperDisplay,
    })

    await cartStore.fetchCart()
  } catch {
    // fail silently
  }
}

const handleSwapConfirmation = async () => {
  await cartStore.fetchCart()
}

const handleDeleteSwap = async () => {
  try {
    await $httpFetch(cartAPI.deleteSwap)
    const eventName = 'delete_swap'

    tracking.trackSwap({
      label: eventName,
      action: 'funnel > Step 4',
      ...(swapStore.offer && swapStore.offer.diagnosticPayload),
    })

    tracking.trackClick({
      name: eventName,
      value: { ...(swapStore.offer && swapStore.offer.diagnosticPayload) },
      zone: 'swap',
    })

    await cartStore.fetchCart()
  } catch {
    // Fails silently
  }
}

const handleCrossSellLoaded = (results) => {
  if (isEmpty(results)) {
    isCrossSellVisible.value = false
  } else {
    isCrossSellVisible.value = true
  }
}

const handleCrossSellRefresh = async (listingId) => {
  await cartStore.fetchCart()
  desiredQuantityPerItem.value[listingId] = `${
    (parseInt(desiredQuantityPerItem.value[listingId], 10) || 0) + 1
  }`
}

const shouldDisplayInstallmentPaymentPrice = (item) => {
  return item.installmentMonthlyPaymentPlan !== null
}
</script>
